import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { BraintreeGatewayComponent } from './braintree-gateway.component';

export interface BraintreeDialogData {
  orderId: number;
  /** 3D Secure params for requestPaymentMethod — { amount, email?, billingAddress? }. */
  threeDSecure?: Record<string, any>;
}

export interface BraintreeDialogResult {
  /** Sale submitted — host should reload the order. */
  paid?: boolean;
  /** Order was no longer payable (e.g. already paid) — host should reload. */
  reload?: boolean;
}

/**
 * Hosts the Braintree Drop-in in a dialog. The order-details payment-box shows
 * a "Pay by card" button that opens this; the Drop-in (card form + Pay button)
 * lives here rather than inline. On success the dialog closes and the host
 * reloads the order to reflect the confirmed/pending status.
 */
@Component({
  selector: 'app-braintree-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule, TranslateModule, BraintreeGatewayComponent],
  template: `
    <h2 mat-dialog-title>{{ 'PAYMENT_PROCESSING.BRAINTREE.TITLE' | translate }}</h2>
    <mat-dialog-content>
      @if (errorMessage()) {
        <div class="bt-error">
          <mat-icon>error</mat-icon>
          <span>{{ errorMessage() }}</span>
        </div>
      }
      <app-braintree-gateway
        [orderId]="data.orderId"
        [threeDSecure]="data.threeDSecure || {}"
        (completed)="onCompleted()"
        (failed)="onFailed($event)" />
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-stroked-button (click)="close()">
        {{ 'PAYMENT_PROCESSING.CLOSE' | translate }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    mat-dialog-content { min-width: 360px; }
    .bt-error {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 12px;
      color: #c0392b;
      font-size: 14px;
    }
    .bt-error mat-icon { font-size: 20px; width: 20px; height: 20px; }
  `]
})
export class BraintreeDialogComponent {
  data: BraintreeDialogData = inject(MAT_DIALOG_DATA);
  private dialogRef = inject(MatDialogRef<BraintreeDialogComponent>);

  errorMessage = signal('');

  onCompleted(): void {
    this.dialogRef.close({ paid: true } as BraintreeDialogResult);
  }

  onFailed(error: { message: string; status?: number }): void {
    if (error.status === 400) {
      // Order no longer payable (often already paid) — let the host reload & confirm.
      this.dialogRef.close({ reload: true } as BraintreeDialogResult);
      return;
    }
    // Declined / transaction failed (422). The gateway already reset the
    // Drop-in for a fresh nonce — show the error and let the buyer retry here.
    this.errorMessage.set(error.message || '');
  }

  close(): void {
    this.dialogRef.close();
  }
}
